// File: Home.jsx
import React, { useEffect, useRef, useState } from "react";
import "./Home.css";

import { supabase } from '../utils/supabaseClient';

/* Dashboard image (replaces the video) */
import dashboardImage from "../assets/dashboard.png";

function HeroVisual() {
  return (
    <div className="hero-visual-wrap">
      <div className="hero-visual">
        <div className="visual-card visual-card-video">
          <div className="visual-header">
            <div className="dot dot-red" />
            <div className="dot dot-yellow" />
            <div className="dot dot-green" />
            <span className="visual-title">Board Meeting Video</span>
            <span className="visual-badge">3h 14m</span>
          </div>

          <div className="playbar">
            <div className="playbar-fill" />
            <div className="playbar-knob" />
          </div>

          <div className="agenda-list">
            <div className="agenda-row">
              <span className="agenda-time">00:12</span>
              <span className="agenda-text">Public Comment</span>
            </div>
            <div className="agenda-row">
              <span className="agenda-time">00:41</span>
              <span className="agenda-text">Math Curriculum Vote</span>
              <span className="agenda-chip chip-blue">Vote</span>
            </div>
            <div className="agenda-row">
              <span className="agenda-time">01:07</span>
              <span className="agenda-text">Budget Amendment</span>
              <span className="agenda-chip chip-gray">Finance</span>
            </div>
            <div className="agenda-row">
              <span className="agenda-time">01:36</span>
              <span className="agenda-text">Title IX Update</span>
              <span className="agenda-chip chip-purple">Policy</span>
            </div>
          </div>

          <div className="visual-footer">
            <div className="pulse" />
            <span>Ingesting & indexing…</span>
          </div>
        </div>

        <div className="visual-card visual-card-summary">
          <div className="summary-top">
            <div className="summary-icon">BW</div>
            <div>
              <div className="summary-title">BoardWatch Summary</div>
              <div className="summary-sub">Evidence-linked recap</div>
            </div>
            <span className="summary-ready">Ready</span>
          </div>

          <div className="summary-lines">
            <div className="line w-90" />
            <div className="line w-80" />
            <div className="line w-70" />
          </div>

          <div className="summary-timestamps">
            <div className="ts-chip">Jump to 00:41</div>
            <div className="ts-chip">Jump to 01:07</div>
            <div className="ts-chip">Jump to 01:36</div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ────────────────────────────────────────────────────────────── */
/* Auto-load logoN.(png|jpg|jpeg|svg) from /src/assets            */
/* ────────────────────────────────────────────────────────────── */
function getSponsorLogos() {
  try {
    const ctx = require.context("../assets", false, /^\.\/logo\d+\.(png|jpe?g|svg)$/);
    const keys = ctx.keys().sort((a, b) => {
      const na = parseInt(a.match(/\d+/)?.[0] || "0", 10);
      const nb = parseInt(b.match(/\d+/)?.[0] || "0", 10);
      return na - nb;
    });
    return keys.map((k) => {
      const mod = ctx(k);
      return typeof mod === "string" ? mod : (mod.default || mod);
    });
  } catch {
    return [];
  }
}
/* ────────────────────────────────────────────────────────────── */
/* Trusted by marquee                                             */
/* ────────────────────────────────────────────────────────────── */
function TrustedByMarquee() {
  const logos = getSponsorLogos();
  if (!logos.length) return null;

  // FIX: Ensure we have enough items to cover wide screens (4k etc.)
  // 1. Create a base set that repeats until it has at least 15 items
  let baseSet = [...logos];
  while (baseSet.length < 15) {
    baseSet = [...baseSet, ...logos];
  }

  // 2. Double that base set. This ensures the first "half" (baseSet)
  // is definitely wider than the screen, allowing the -50% translation
  // to happen seamlessly without showing a gap.
  const looped = [...baseSet, ...baseSet];

  return (
    <section className="trustedby" aria-label="Trusted by parent groups and nonprofits">
      <div className="trustedby__inner">
        <div className="trustedby__title">Trusted by districts &amp; nonprofits</div>
        <div className="trustedby__marquee" role="list" aria-label="Organizations that use BoardWatch">
          <div className="trustedby__track">
            {looped.map((src, i) => (
              <div className="trustedby__item" role="listitem" key={`${src}-${i}`}>
                {/* Added height/width to img to prevent layout shifts/jumping before load */}
                <img 
                  src={src}
                  alt={`Partner logo`}
                  loading="lazy"
                  height="48"
                  width="auto"
                />
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
/* Icons for the feature tiles */
const BWIconSummary = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M4 5h16v14H4z" fill="none" stroke="currentColor" strokeWidth="1.5" />
    <path d="M7 9h10M7 12h7M7 15h6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);
const BWIconTopics = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <circle cx="6" cy="8" r="2" />
    <circle cx="6" cy="16" r="2" />
    <path d="M10 8h8M10 16h8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" fill="none" />
  </svg>
);
const BWIconVoting = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M4 10h16v9H4z" fill="none" stroke="currentColor" strokeWidth="1.5" />
    <path d="M9 10l3-5h3l-3 5" stroke="currentColor" strokeWidth="1.5" fill="none" />
    <path d="M7 14h3M12 14h5M7 17h8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);
const BWIconSearch = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <circle cx="11" cy="11" r="6" fill="none" stroke="currentColor" strokeWidth="1.8" />
    <path d="M16.5 16.5L21 21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

/* ────────────────────────────────────────────────────────────── */
/* Motion utilities                                               */
/* ────────────────────────────────────────────────────────────── */
function usePrefersReducedMotion() {
  const [prefers, setPrefers] = React.useState(false);
  React.useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const onChange = () => setPrefers(mq.matches);
    setPrefers(mq.matches);
    if (mq.addEventListener) mq.addEventListener("change", onChange);
    else mq.addListener(onChange);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", onChange);
      else mq.removeListener(onChange);
    };
  }, []);
  return prefers;
}

/** Gate an interaction until element is on-screen */
function useOnScreen(ref, { root = null, rootMargin = "0px", threshold = 0.25 } = {}) {
  const [isOnScreen, setIsOnScreen] = React.useState(false);
  React.useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setIsOnScreen(true); // one-shot
      },
      { root, rootMargin, threshold }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [ref, root, rootMargin, threshold]);
  return isOnScreen;
}

/** Typewriter that can be activated on demand */
function TypewriterTitle({ prefix, accent, startDelay = 400, speed = 40, active = true }) {
  const reduce = usePrefersReducedMotion();
  const [i, setI] = React.useState(0);
  const [typingAccent, setTypingAccent] = React.useState(false);

  React.useEffect(() => {
    if (!active || reduce) return;
    const fullPrefix = (prefix ?? "");
    const fullAccent = (accent ?? "");

    const targetLen = typingAccent ? fullAccent.length : fullPrefix.length;
    if (i >= targetLen && typingAccent) return;

    const timer = setTimeout(() => {
      if (!typingAccent) {
        if (i < fullPrefix.length) {
          setI(i + 1);
        } else {
          setTypingAccent(true);
          setI(0);
        }
      } else {
        if (i < fullAccent.length) setI(i + 1);
      }
    }, (i === 0 && !typingAccent) ? startDelay : speed);

    return () => clearTimeout(timer);
  }, [active, reduce, i, typingAccent, prefix, accent, startDelay, speed]);

  if (reduce) {
    return (
      <>
        {prefix}
        <span className="accent-blue">{accent}</span>
      </>
    );
  }

  // Reserve space before activation to avoid layout shift
  if (!active) {
    return (
      <span className="typewriter" aria-hidden="true" style={{ opacity: 0 }}>
        {prefix}
        <span className="accent-blue">{accent}</span>
      </span>
    );
  }

  const fullPrefix = prefix ?? "";
  const fullAccent = accent ?? "";
  const showCursor =
    (!typingAccent && i < fullPrefix.length) ||
    (typingAccent && i < fullAccent.length);

  return (
    <span className="typewriter" aria-live="polite">
      {typingAccent ? fullPrefix : fullPrefix.slice(0, i)}
      <span className="accent-blue">
        {typingAccent ? fullAccent.slice(0, i) : ""}
      </span>
      {showCursor && <span className="type-cursor" aria-hidden="true">|</span>}
    </span>
  );
}

/* Demo (image) section */
function BWDemoSection() {
  const headRef = React.useRef(null);
  // Fire a little early so users actually see the typing start
  const startTyping = useOnScreen(headRef, { rootMargin: "0px 0px -35% 0px", threshold: 0.2 });

  return (
    <section className="bw-showcase" aria-label="BoardWatch live demo">
      <div className="bw-showcase__inner">
        <header className="bw-showcase__head" ref={headRef}>
          <div className="bw-head-left">
            <span className="chip chip-live" aria-hidden="true">Live demo</span>
            <h2 className="bw-head-title">
              <TypewriterTitle
                prefix="See it in action: "
                accent="Palo Alto USD"
                active={startTyping}
              />
            </h2>
            <p className="bw-head-sub">Real meeting data analyzed by BoardWatch.</p>
          </div>
          <div className="bw-head-right">
            <a
              className="btn-cta is-lg btn-cta--ghostdemo"
              href="/contactus"
              aria-label="Get BoardWatch in your district"
            >
              <span>Set up my district</span>
              <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
                <path d="M5 12h12M13 6l6 6-6 6" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </a>
          </div>
        </header>

        <div className="bw-panel" aria-label="BoardWatch demo">
          <div className="bw-video-container">
            <div className="bw-demo-card">
              <div className="bw-demo-card__surface">
                <img
                  className="bw-demo-image"
                  src={dashboardImage}
                  alt="BoardWatch dashboard preview"
                  loading="lazy"
                />
              </div>
            </div>
          </div>

        {/* concise feature band under the image */}
          <div className="bw-feature-band" role="list">
            <div className="bw-feature" role="listitem">
              <span className="bw-feature__icon"><BWIconSearch /></span>
              <h3>Semantic Search</h3>
              <p>Instantly find key moments across school board meetings. Search topics like “student safety” with direct video timestamps.</p>
            </div>
            <div className="bw-feature" role="listitem">
              <span className="bw-feature__icon"><BWIconSummary /></span>
              <h3>Meetings</h3>
              <p>AI-generated overviews of every school board meeting, highlighting key discussions and decisions so your community can stay informed.</p>
            </div>
            <div className="bw-feature" role="listitem">
              <span className="bw-feature__icon"><BWIconTopics /></span>
              <h3>Topics</h3>
              <p>Track issues you care about such as curriculum, budgets, and staffing, and see topic trends over years of meetings.</p>
            </div>
            <div className="bw-feature" role="listitem">
              <span className="bw-feature__icon"><BWIconVoting /></span>
              <h3>AI Assistant</h3>
              <p>Instantly get answers to questions regarding board meetings. Ask in plain language to uncover insights without manual searching.</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
/* ────────────────────────────────────────────────────────────── */
/* UPDATED: BoardWatch API for Organizations (Supabase)          */
/* ────────────────────────────────────────────────────────────── */
function APIAccessSection() {
  const [org, setOrg] = useState("");
  const [website, setWebsite] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [notes, setNotes] = useState("");

  const [loading, setLoading] = useState(false);
  const [feedback, setFeedback] = useState(null); // 'success' | 'error' | null

  const handleSupabaseSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setFeedback(null);

    // Basic validation
    if (!org || !email || !name) {
      setLoading(false);
      alert("Please fill in the required fields (District Name, Name, Email).");
      return;
    }

    try {
      const { error } = await supabase
        .from('inbound_requests')
        .insert([
          { 
            organization: org, 
            website: website, 
            contact_name: name, 
            contact_email: email, 
            notes: notes 
          }
        ]);

      if (error) throw error;

      setFeedback('success');
      // Clear form on success
      setOrg("");
      setWebsite("");
      setName("");
      setEmail("");
      setNotes("");
    } catch (error) {
      console.error('Error submitting form:', error);
      setFeedback('error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="setup" aria-label="BoardWatch API for organizations">
      <div className="setup__inner">
        <div className="setup__grid">
          {/* Left copy */}
          <div className="setup__left">
            <span className="chip chip-live" aria-hidden="true">Enterprise Governance</span>
            <h2 className="setup__title">Scale your Community’s Board Awareness</h2>
            <p className="setup__sub">
            Save hours of research time. We turn long school board meetings into a searchable update hub your parent group can publish and share instantly.            </p>

          <ul className="setup__bullets">
            <li><strong>Meeting Visibility:</strong> Turn multi-hour meetings into quick, easy-to-read updates.</li>
            <li><strong>Source Transparency:</strong> Every summary includes direct timestamps to the exact video moment.</li>
            <li><strong>Labor Savings:</strong> Skip manual note-taking and video scrubbing for each meeting.</li>
          </ul>

            <div className="setup__actions">
              <a className="btn-cta is-md" href="https://board-watch.com/about">
                Learn More
                <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
                  <path d="M5 12h12M13 6l6 6-6 6" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </a>
            </div>
          </div>

          {/* Right: API contact form */}
          <div className="setup__right" aria-label="API interest form">
            <form className="setup__form" onSubmit={handleSupabaseSubmit}>
              <div className="setup__row">
                <label>
                  <span>District Name</span>
                  <input 
                    value={org} 
                    onChange={(e) => setOrg(e.target.value)} 
                    placeholder="Your organization" 
                    required 
                  />
                </label>
              </div>

              <div className="setup__row">
                <label>
                  <span>Website / domain</span>
                  <input value={website} onChange={(e) => setWebsite(e.target.value)} placeholder="https://www.example.org" />
                </label>
              </div>

              <div className="setup__row setup__row--2">
                <label>
                  <span>Primary contact name</span>
                  <input 
                    value={name} 
                    onChange={(e) => setName(e.target.value)} 
                    placeholder="Your name" 
                    required 
                  />
                </label>
                <label>
                  <span>Email</span>
                  <input 
                    type="email" 
                    value={email} 
                    onChange={(e) => setEmail(e.target.value)} 
                    placeholder="you@example.org" 
                    required 
                  />
                </label>
              </div>

              <div className="setup__row">
                <label>
                  <span>Notes (optional)</span>
                  <textarea
                    rows={4}
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Anything we should know about your coverage targets, timeline, or constraints."
                  />
                </label>
              </div>

              <div className="setup__submit">
                <button 
                  className="btn-cta is-lg" 
                  type="submit"
                  disabled={loading || feedback === 'success'}
                >
                   {loading ? "Sending..." : feedback === 'success' ? "Sent!" : "Send request"}
                </button>
              </div>

              {/* Feedback Messages */}
              {feedback === 'success' && (
                <div style={{ marginTop: '1rem', color: '#10b981', fontSize: '0.9rem', fontWeight: 500 }}>
                  Thanks! We've received your request and will be in touch shortly.
                </div>
              )}
              {feedback === 'error' && (
                <div style={{ marginTop: '1rem', color: '#ef4444', fontSize: '0.9rem', fontWeight: 500 }}>
                  Something went wrong. Please try again or email us directly.
                </div>
              )}
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ────────────────────────────────────────────────────────────── */
/* NEW: FAQ section (no outer container)                          */
/* ────────────────────────────────────────────────────────────── */
function FAQSection() {
  const faqs = [
    {
      q: "What is BoardWatch in a sentence?",
      a: "A neutral layer that turns school board meetings and agendas into clear summaries, searchable topics, and transparent voting records for families."
    },
    {
      q: "Can we embed BoardWatch on our website or get API access?",
      a: "Yes. Most orgs embed components (summaries, topics, voting) via our API. Choose fully embedded blocks or raw JSON to power your own UI."
    },
    {
      q: "How long does it take to launch for our district?",
      a: "Typical setup is 1–2 weeks once we have board links (YouTube/BoardDocs/agenda sources). Timelines vary with archival depth and custom requests."
    },
    {
      q: "How does pricing work?",
      a: "Pricing reflects coverage scope (district size, meeting cadence, historical backfill) and whether you want website embeds, raw API, or both."
    },
    {
      q: "What sources do you use?",
      a: "We ingest public materials such as videos, audio, agendas, minutes, and vote logs, then link back to originals for verification."
    },
    {
      q: "Is the content neutral?",
      a: "Yes. Reports aim for clarity and traceability with timestamps and citations to source materials."
    }
  ];

  // CHANGED: start with no item open
  const [openIndex, setOpenIndex] = React.useState(-1);

  return (
    <section className="faq" aria-label="Frequently asked questions">
      <header className="faq__head">
        <span className="chip chip-live" aria-hidden="true">FAQ</span>
        <h2 className="faq__title">Frequently asked questions</h2>
        <p className="faq__sub">
          Answers about setup, access, timelines, and privacy.
        </p>
        <div className="faq__rule" aria-hidden="true" />
      </header>

      <div className="faq__list" role="list">
        {faqs.map((item, i) => {
          const isOpen = i === openIndex;
          const btnId = `faq-btn-${i}`;
          const panelId = `faq-panel-${i}`;
          return (
            <div className={"faq__item" + (isOpen ? " is-open" : "")} role="listitem" key={i}>
              <button
                id={btnId}
                className="faq__question"
                aria-expanded={isOpen}
                aria-controls={panelId}
                onClick={() => setOpenIndex(isOpen ? -1 : i)}
              >
                <span className="faq__qtext">{item.q}</span>
                <span className="faq__icon" aria-hidden="true">
                  <svg viewBox="0 0 24 24" width="18" height="18">
                    <path className="line-vert" d="M12 5v14" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                    <path className="line-horz" d="M5 12h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                </span>
              </button>
              <div
                id={panelId}
                className="faq__answer"
                role="region"
                aria-labelledby={btnId}
              >
                <p>{item.a}</p>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

/* ── Reveal-on-scroll utilities ───────────────────────────────── */
function useRevealOnScroll(ref, rootMargin = "0px 0px -10% 0px") {
  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) el.classList.add("is-visible");
        });
      },
      { root: null, rootMargin, threshold: 0.1 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [ref, rootMargin]);
}

function Reveal({ as: Tag = "div", className = "", children, margin }) {
  const r = useRef(null);
  useRevealOnScroll(r, margin);
  return (
    <Tag ref={r} className={`reveal ${className}`}>
      {children}
    </Tag>
  );
}

export default function Home(props) {
  const {
    heroExtra = "0px",
    subtitleOffset = "10px",
    shineGradientOpacity = 0.76,
    shineAnimOpacity = 0.5,
    shineWidth = "60%",
    shineBlur = "14px",
    dashboardMaxWidth = "980px"
  } = props;

  return (
    <div
      className="home-page"
      style={{
        "--hero-extra-space": heroExtra,
        "--sub-offset": subtitleOffset,
        "--shine-gradient-opacity": shineGradientOpacity,
        "--shine-anim-opacity": shineAnimOpacity,
        "--shine-width": shineWidth,
        "--shine-blur": shineBlur,
        "--dashboard-max-width": dashboardMaxWidth,
        "--showcase-offset-y": "32px",
        "--trustedby-offset-y": "28px", 
      }}
    >
{/* HERO (from About) */}
<section className="about-hero home-hero">
  <div className="about-hero-inner">
    <div className="about-hero-text">
      <div className="about-kicker">About BoardWatch</div>
      <h1 className="about-title">
        School board meetings,
        <br />
        finally easy to search.
      </h1>

      <p className="about-subtitle">
      BoardWatch turns hours of complex board meeting video into clear, trustworthy updates for your entire network, with integrated citations that jump to the exact moment in the footage.
      </p>

      <div className="about-cta-row">
        <a href="/contactus" className="btn-primary">Contact sales</a>
        <a href="/districts/suhsd" className="btn-ghost">See live demo</a>
        <a href="/districts/flas" className="btn-ghost">See Forest Lake</a>
      </div>

      <div className="about-metrics">
        <div className="metric">
          <div className="metric-value">50×</div>
          <div className="metric-label">faster meeting analysis</div>
        </div>
        <div className="metric">
          <div className="metric-value">1-click</div>
          <div className="metric-label">video evidence jumps</div>
        </div>
        <div className="metric">
          <div className="metric-value">US-wide</div>
          <div className="metric-label">district coverage</div>
        </div>
      </div>
    </div>

    <div className="about-hero-media">
      <HeroVisual />
    </div>
  </div>
</section>

      {/* Trusted by marquee */}
      <TrustedByMarquee />

      {/* Demo + features section (trigger reveal early so stagger is seen) */}
      <Reveal as="div" margin="0px 0px -12% 0px"><BWDemoSection /></Reveal>

      {/* NEW: Set up my district */}
      <Reveal as="div"><APIAccessSection /></Reveal>

      {/* NEW: FAQ (below setup) */}
      <Reveal as="div"><FAQSection /></Reveal>
    </div>
  );
}